/**
 * Espurna — Caliu's own icon set, replacing Lucide (web) and SF Symbols (Swift).
 *
 * Source of truth: `catalog.json` + `svg/<id>.svg` in this folder. Every SVG
 * has one `line` layer (the drawing). There is no filled or active variant.
 * Rules for drawing new icons: see the Icons section of packages/styles/AGENTS.md.
 */

import catalog from "./catalog.json";

export type IconGroup = "nav" | "note" | "editor" | "share" | "account" | "time" | "system" | "tags";

export interface IconCatalogEntry {
  /** Stable id, also the SVG filename. */
  id: string;
  group: IconGroup;
  /** English description of what the icon means. */
  label: string;
  /** Lucide icon this replaces in apps/app, or the nearest one; empty when Lucide has none. */
  lucide: string;
  /** SF Symbols this replaces in apps/swift (fill/slash variants included). */
  sf: string[];
  /** Synced tag-picker key (`Tag.icon`) this icon renders, if any. */
  tag: string | null;
  /** Tag-picker category this icon is listed under. Set exactly when `tag` is. */
  tagCategory: TagCategoryId | null;
}

export type TagCategoryId = "essentials" | "work" | "ideas" | "life" | "travel" | "fun";

export interface TagCategory {
  id: TagCategoryId;
  /** English name shown as the picker section title. */
  name: string;
  /** English one-line description of what belongs in it. */
  description: string;
}

/** Tag-picker categories, in display order. Keep this list at six or fewer. */
export const TAG_CATEGORIES: TagCategory[] = [
  { id: "essentials", name: "Essentials", description: "Markers and plain shapes: what matters, what's done, what's urgent." },
  { id: "work", name: "Work & tech", description: "Projects, meetings, code and the devices around them." },
  { id: "ideas", name: "Ideas & creativity", description: "Study, research, and making things: art, music, photos, words." },
  { id: "life", name: "People & home", description: "Friends, family, the house and the money that runs it." },
  { id: "travel", name: "Travel & nature", description: "Trips, places, the garden, the seasons and the sky." },
  { id: "fun", name: "Food, health & fun", description: "Recipes, training, games and lazy evenings." },
];

export const iconCatalog = catalog as IconCatalogEntry[];

export const iconsById: Record<string, IconCatalogEntry> = Object.fromEntries(
  iconCatalog.map((icon) => [icon.id, icon]),
);

/** Tag-picker key → icon id. Keys match `TAG_ICONS` (web) and `TagAppearanceCatalog` (Swift). */
export const iconIdByTagKey: Record<string, string> = Object.fromEntries(
  iconCatalog.filter((icon) => icon.tag).map((icon) => [icon.tag as string, icon.id]),
);

/** Tag-picker icons grouped by category, in catalog order within each category. */
export const tagIconsByCategory: Record<TagCategoryId, IconCatalogEntry[]> = Object.fromEntries(
  TAG_CATEGORIES.map((category) => [category.id, iconCatalog.filter((icon) => icon.tagCategory === category.id)]),
) as Record<TagCategoryId, IconCatalogEntry[]>;
